import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card } from '../../design-system/components/Card';
import { Spinner } from '../../design-system/components/Spinner';
import { toApiError } from '../../shared/http/errors';
import { leddoCallback } from './api';
import { useAuthStore } from './store';

/**
 * Tela de callback OAuth do Leddo.
 *
 * Rota pública `/auth/leddo/callback` — sem ProtectedRoute, pois o usuário
 * ainda não está autenticado quando chega aqui vindo do redirect do Leddo.
 *
 * Lê `code` e `state` da query string, troca pelo par de tokens no backend,
 * popula a store e redireciona para o destino original (ou `/`).
 */
export function LeddoCallbackScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const loginWithLeddoTokens = useAuthStore((s) => s.loginWithLeddoTokens);

  const [erro, setErro] = useState<string | null>(null);
  // O `state` do OAuth é de uso único (consumido no backend). Sob React
  // StrictMode (dev) o effect roda 2×; sem este guard, as duas execuções
  // chamam o callback com o mesmo `state` → a 2ª recebe "state consumido"
  // ("Sessão de login expirou"). O ref garante UMA troca por montagem.
  const jaIniciou = useRef(false);

  useEffect(() => {
    if (jaIniciou.current) return;
    jaIniciou.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code === null || state === null) {
      setErro('Parâmetros de callback ausentes. Tente fazer login novamente.');
      return;
    }

    const trocarTokens = async () => {
      try {
        const tokens = await leddoCallback(code, state);
        await loginWithLeddoTokens(tokens);
        const from = (location.state as { from?: string } | null)?.from ?? '/';
        navigate(from, { replace: true });
      } catch (err) {
        setErro(toApiError(err).message);
      }
    };

    void trocarTokens();
  }, []);

  if (erro !== null) {
    return (
      <main className="login-screen">
        <Card className="login-card">
          <h1 className="login-title">Leddo Copilot</h1>
          <p className="login-error" role="alert">
            {erro}
          </p>
        </Card>
      </main>
    );
  }

  return (
    <main className="login-screen">
      <Card className="login-card">
        <Spinner label="Autenticando com Leddo…" />
      </Card>
    </main>
  );
}
